import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowRight, Repeat } from 'lucide-react';
import { FormSheet } from '@/components/common/FormSheet';
import { FormField } from '@/components/common/FormField';
import { DialogEyebrow } from '@/components/common/DialogPrimitives';
import { DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  inventoryService,
  warehousesService,
} from '@/services/inventory';
import { fmt } from '@/lib/format';
import { pickerLabel } from '@/lib/inventory-label';
import type { InventoryItem } from '@/types/inventory';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pre-fill the item picker when opened from a per-item context. */
  prefillItem?: InventoryItem | null;
}

/**
 * Stock transfer between two warehouses, atomic. Operator picks SKU →
 * source warehouse → destination warehouse → quantity (in selected UoM).
 * FE converts to base units before submit. The BE writes a paired
 * TRANSFER_OUT + TRANSFER_IN movement in one transaction.
 *
 * Closes the most-cited inventory ops pain — previously this took two
 * separate adjustments with no atomicity guarantee.
 */
export function TransferStockSheet({ open, onOpenChange, prefillItem }: Props) {
  const qc = useQueryClient();

  const [itemId, setItemId] = useState<string>('');
  const [fromWarehouseId, setFromWarehouseId] = useState<string>('');
  const [toWarehouseId, setToWarehouseId] = useState<string>('');
  const [quantity, setQuantity] = useState('');
  const [unitId, setUnitId] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  const itemsQuery = useQuery({
    queryKey: ['inventory', 'items', { status: 'ACTIVE', forSelect: 'transfer', pickable: true }],
    queryFn: () => inventoryService.list({ status: 'ACTIVE', pageSize: 200, pickable: true }),
    enabled: open,
    staleTime: 30_000,
  });

  const warehousesQuery = useQuery({
    queryKey: ['inventory', 'warehouses'],
    queryFn: () => warehousesService.list(),
    enabled: open,
    staleTime: 60_000,
  });

  const items = itemsQuery.data?.items ?? [];
  const warehouses = warehousesQuery.data ?? [];
  const item = useMemo(() => items.find((i) => i.id === itemId) ?? null, [items, itemId]);

  // Per-item alternate UoMs.
  const unitsQuery = useQuery({
    queryKey: ['inventory', 'units', itemId],
    queryFn: () => inventoryService.listUnits(itemId),
    enabled: !!itemId && open,
    staleTime: 30_000,
  });
  const units = unitsQuery.data ?? [];

  // Per-item per-warehouse stock so we can show "available at source".
  const stockQuery = useQuery({
    queryKey: ['inventory', 'stock', itemId],
    queryFn: () => inventoryService.listStock({ itemId, pageSize: 100 }),
    enabled: !!itemId && open,
  });
  const sourceStock = stockQuery.data?.rows.find((r) => r.warehouseId === fromWarehouseId);
  const sourceAvailable = sourceStock
    ? Number(sourceStock.quantityOnHand) - Number(sourceStock.quantityReserved)
    : 0;

  // Reset on open / prefill.
  useEffect(() => {
    if (!open) return;
    setItemId(prefillItem?.id ?? '');
    setFromWarehouseId('');
    setToWarehouseId('');
    setQuantity('');
    setUnitId(null);
    setReason('');
  }, [open, prefillItem]);

  // Compute base-unit quantity that will actually move.
  const selectedUnit = unitId ? units.find((u) => u.id === unitId) ?? null : null;
  const rawQty = Number(quantity);
  const baseQty =
    Number.isFinite(rawQty) && rawQty > 0
      ? selectedUnit
        ? rawQty * Number(selectedUnit.conversionFactor)
        : rawQty
      : 0;
  const baseUnit = item?.unitOfMeasure ?? '';
  const overdraw = baseQty > sourceAvailable && fromWarehouseId && itemId;

  const mutation = useMutation({
    mutationFn: () =>
      inventoryService.transferStock({
        itemId,
        fromWarehouseId,
        toWarehouseId,
        quantity: baseQty,
        reason: reason.trim() || undefined,
      }),
    onSuccess: () => {
      toast.success(
        `Moved ${fmt.number(baseQty)} ${baseUnit} to destination warehouse`,
      );
      qc.invalidateQueries({ queryKey: ['inventory'] });
      qc.invalidateQueries({ queryKey: ['inventory', 'stock'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      onOpenChange(false);
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not transfer stock'),
  });

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!itemId || !fromWarehouseId || !toWarehouseId || baseQty <= 0) return;
    if (fromWarehouseId === toWarehouseId) {
      toast.error('Source and destination warehouses must differ.');
      return;
    }
    if (overdraw) return;
    mutation.mutate();
  };

  const valid =
    !!itemId &&
    !!fromWarehouseId &&
    !!toWarehouseId &&
    fromWarehouseId !== toWarehouseId &&
    baseQty > 0 &&
    !overdraw;

  return (
    <FormSheet
      open={open}
      onOpenChange={onOpenChange}
      headerSlot={
        <div className="flex items-start gap-4">
          <span
            aria-hidden="true"
            className="grid h-14 w-14 shrink-0 place-items-center rounded-lg bg-gradient-to-br from-primary/10 to-primary/5 text-primary ring-1 ring-inset ring-primary/15"
          >
            <Repeat className="h-6 w-6" />
          </span>
          <div className="min-w-0 flex-1 space-y-1.5">
            <DialogEyebrow>Stock transfer</DialogEyebrow>
            <DialogTitle className="text-[18px] font-semibold leading-tight tracking-tight">
              Move stock between warehouses
            </DialogTitle>
            <DialogDescription className="text-[12.5px] leading-relaxed">
              Atomic — one transaction writes both the TRANSFER_OUT (source) and
              TRANSFER_IN (destination) ledger rows. Quantities entered in any
              UoM are converted to base before submit.
            </DialogDescription>
          </div>
        </div>
      }
      title=""
      submitLabel="Transfer stock"
      onSubmit={onSubmit}
      isSubmitting={mutation.isPending}
      error={mutation.error}
      size="3xl"
    >
      <FormField label="Item" htmlFor="itemId" required>
        <Select
          value={itemId}
          onValueChange={(v) => {
            setItemId(v);
            setUnitId(null);
            setFromWarehouseId('');
            setToWarehouseId('');
          }}
        >
          <SelectTrigger id="itemId" className="h-9">
            <SelectValue placeholder={itemsQuery.isLoading ? 'Loading SKUs…' : 'Select SKU'} />
          </SelectTrigger>
          <SelectContent>
            {items.map((it) => (
              <SelectItem key={it.id} value={it.id}>
                <span className="font-mono text-[11px] text-muted-foreground">{it.sku}</span>
                <span className="ml-2">{pickerLabel(it)}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FormField>

      <div className="grid grid-cols-12 items-end gap-3">
        <div className="col-span-5">
          <FormField label="From warehouse" htmlFor="fromWh" required>
            <Select value={fromWarehouseId} onValueChange={setFromWarehouseId}>
              <SelectTrigger id="fromWh" className="h-9">
                <SelectValue placeholder="Source" />
              </SelectTrigger>
              <SelectContent>
                {warehouses.map((w) => (
                  <SelectItem key={w.id} value={w.id}>
                    <span className="font-mono text-[11px] text-muted-foreground">{w.code}</span>
                    <span className="ml-2">{w.name}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>
        </div>
        <div className="col-span-2 pb-2 text-center text-muted-foreground">
          <ArrowRight className="mx-auto h-4 w-4" />
        </div>
        <div className="col-span-5">
          <FormField label="To warehouse" htmlFor="toWh" required>
            <Select value={toWarehouseId} onValueChange={setToWarehouseId}>
              <SelectTrigger id="toWh" className="h-9">
                <SelectValue placeholder="Destination" />
              </SelectTrigger>
              <SelectContent>
                {warehouses
                  .filter((w) => w.id !== fromWarehouseId)
                  .map((w) => (
                    <SelectItem key={w.id} value={w.id}>
                      <span className="font-mono text-[11px] text-muted-foreground">{w.code}</span>
                      <span className="ml-2">{w.name}</span>
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </FormField>
        </div>
      </div>

      {fromWarehouseId && itemId && (
        <div className="rounded-md border border-border/70 bg-card px-3 py-2 text-[11.5px] text-muted-foreground">
          Available at source:{' '}
          <span className="font-mono tabular-nums text-foreground">
            {fmt.number(sourceAvailable)} {baseUnit}
          </span>
          {sourceStock && Number(sourceStock.quantityReserved) > 0 && (
            <span>
              {' '}
              · {fmt.number(Number(sourceStock.quantityReserved))} reserved (excluded)
            </span>
          )}
        </div>
      )}

      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-7">
          <FormField label="Quantity" htmlFor="quantity" required>
            <Input
              id="quantity"
              type="number"
              inputMode="decimal"
              min={0}
              step="0.001"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              placeholder="0"
              className="h-9 text-right font-mono tabular-nums"
            />
          </FormField>
        </div>
        <div className="col-span-5">
          <FormField label="UoM" htmlFor="unitId">
            <Select
              value={unitId ?? '__base'}
              onValueChange={(v) => setUnitId(v === '__base' ? null : v)}
              disabled={!itemId}
            >
              <SelectTrigger id="unitId" className="h-9 font-mono text-[12.5px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__base">
                  <span className="font-mono">{baseUnit || '—'}</span>
                  <span className="ml-2 text-muted-foreground">base</span>
                </SelectItem>
                {units.map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    <span className="font-mono">{u.code}</span>
                    <span className="ml-2 text-muted-foreground">
                      ×{fmt.number(u.conversionFactor)}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>
        </div>
      </div>

      {selectedUnit && baseQty > 0 && (
        <div className="rounded-md border border-info/20 bg-info/5 px-3 py-2 text-[11.5px] text-muted-foreground">
          ↳ moves{' '}
          <span className="font-mono tabular-nums text-foreground">
            {fmt.number(baseQty)} {baseUnit}
          </span>{' '}
          out of source and into destination.
        </div>
      )}

      {overdraw && (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-[12px] text-destructive">
          Quantity {fmt.number(baseQty)} {baseUnit} exceeds available{' '}
          {fmt.number(sourceAvailable)} {baseUnit} at the source warehouse.
        </div>
      )}

      <FormField
        label="Reason / reference"
        htmlFor="reason"
        hint="Optional — appears in both ledger rows + audit trail."
      >
        <Textarea
          id="reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Rebalancing inventory · Customer pickup at MUM-W2 · GRN handoff"
          rows={2}
        />
      </FormField>

      {!valid && itemId && fromWarehouseId && toWarehouseId && (
        <div className="rounded-md border border-warning/30 bg-warning/5 px-3 py-2 text-[11.5px] text-[hsl(38_85%_30%)]">
          Enter a quantity greater than 0 (and within available source stock).
        </div>
      )}
    </FormSheet>
  );
}
